import type { ApolloClient } from '@apollo/client';
import { auth, signOut } from '@/lib/firebase';
import { clearSsoCookies } from '@/lib/sso-cookies';
import { clearSamlSession } from '@/lib/saml-session';
import { resetDDPClient } from '@scio3/ddp-client';
import { clearPersistedCache } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useScreenSelectionStore } from '@/features/screens/stores/screen-selection-store';
import { useScreenFilterStore } from '@/features/screens/stores/screen-filter-store';
import { useAssetSelectionStore } from '@/features/assets/stores/asset-selection-store';
import { usePlaylistSelectionStore } from '@/features/playlists/stores/playlist-selection-store';
import { useAnalyticsFilterStore } from '@/features/analytics/stores/analytics-filter-store';
import { useScheduleSelectionStore } from '@/features/schedules/stores/schedule-selection-store';
import { useFavoritesStore } from '@/stores/favorites-store';
import { clearFileCdnCache } from '@/lib/file-cdn';
import { clearAssetDefCache } from '@/features/assets/utils/app-meta';
import { clearBroadcastBannerDismissals } from '@/features/broadcasts/hooks/useBannerDismissal';
import { SHARE_TOKEN_KEY } from '@/lib/share-token';
import { clearConsumedIntent } from '@/lib/app-intent';
import { clearSavedIds } from '@/hooks/useDeepLinkRestore';
import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import { resetPickerPath } from '@/features/playlists/utils/picker-path';
import { analyticsLogout } from '@/lib/analytics-service';
import { clearCacheOwnerStorage } from '@/lib/cache-owner';

/** Guard against re-entrant fullLogout calls (e.g. onAuthStateChanged triggers during signOut) */
let logoutInProgress = false;

/**
 * Clear everything scoped to the current ACCOUNT while keeping the signed-in identity.
 *
 * Used directly when the same user moves to another account without signing out (accepting a
 * cross-account invite, #2387 — the previous account may even be deleted), and by fullLogout.
 * Nothing account-scoped may survive into the next session: Apollo cache in memory, its
 * IndexedDB copy and cache-owner key, persisted team, folder paths, deep links, OptiBot chats,
 * share tokens, per-account UI state. Identity — Firebase session, SSO cookies, Meteor login
 * token, magic-link email, OneDrive login hint, the in-memory auth session — is the same person
 * and is cleared only by fullLogout. A caller switching accounts must then load a fresh page.
 */
export async function clearAccountData(client: ApolloClient): Promise<void> {
  // SAML session belongs to the account's IdP.
  clearSamlSession();

  // Clear Apollo in-memory cache (prevents stale data on next login)
  await client.clearStore();

  // Clear persisted cache (IndexedDB) — SECURITY: prevent data exposure
  await clearPersistedCache();

  // Clear cache owner (localStorage) — prevents stale owner from restoring wrong cache key
  clearCacheOwnerStorage();

  // Reset DDP client (close Meteor subscriptions)
  resetDDPClient();

  // Broadcast banner "closed until next login" marks — the next session starts clean.
  clearBroadcastBannerDismissals();

  // User/account-specific data that could leak across accounts or on shared computers
  localStorage.removeItem('asset-search-history');
  localStorage.removeItem('scio3-get-started-completed');

  // Clear version redirect loop guard (stale guard could block a legitimate redirect within 10s)
  localStorage.removeItem('scio_ver_redirect');

  // Clear file CDN signed URL cache
  clearFileCdnCache();

  // Clear asset definitions cache
  clearAssetDefCache();

  // Clear OptiBot chat sessions (prevent cross-user/account chat leakage)
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith('optibot-session-'))
    .forEach((k) => sessionStorage.removeItem(k));

  // Clear share-link sessionStorage (cached share token + temp login session).
  // Without this, revisiting `/share/folder/X` would resurface a cached token via
  // getExistingToken → backend rejects (revoked / wrong identity) → 401 with no clear recovery
  // path. Temp login session key is inline (defined in SignInSharePage feature module — pulling
  // it here would require an unnecessary feature → lib reverse import).
  sessionStorage.removeItem(SHARE_TOKEN_KEY);
  sessionStorage.removeItem('___SHARE_LINK_TEMP_LOGIN_SESSION___');

  // Clear the Group Security §12 home-land session guards so the NEXT login re-runs the one-time
  // home-team switch + folder land (inline keys — pulling the hook here would be a feature→lib
  // reverse import; keep in sync with useAutoLandFolder).
  sessionStorage.removeItem('scio3:homeLandPending');
  sessionStorage.removeItem('scio3:homeLandedFor');

  // Clear the consumed dashboard-app intent. It is read inside `analyticsScreenPaired`
  // to attribute a pairing back to the `/signUp?app=` link that started the session, so
  // leaving it behind would decorate the NEXT session's `screen_paired` with this attribution.
  clearConsumedIntent();
  // Clear QA experiment force-overrides (`?ws=`, `?force_variant_<key>=`).
  // They live in per-TAB sessionStorage, not per-account, so without this a forced
  // arm set once follows every subsequent account signed in from the same window: a
  // fresh signup silently inherits the previous subject's arm and masks whatever the
  // decider actually assigned. Prefix is inlined for the same reason as the
  // share-link keys above (defined in features/experiments/utils/force-variant.ts —
  // importing it here would be a lib -> feature reverse import).
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith('experiment-force:'))
    .forEach((k) => sessionStorage.removeItem(k));

  // Clear persisted folder paths (prevent cross-account path leakage)
  clearPersistedPaths();

  // Clear deep-link IDs (module-level variable survives SPA navigation)
  clearSavedIds();

  // Clear in-memory + persisted team-store — prevent stale data bleeding across accounts
  useTeamStore.getState().clearTeams();
  useTeamStore.persist.clearStorage();

  // Clear feature selection stores
  useScreenSelectionStore.getState().clearSelection();
  useAssetSelectionStore.getState().clearSelection();
  usePlaylistSelectionStore.getState().clearSelection();
  usePlaylistSelectionStore.getState().setActivePlaylist(null);
  useScheduleSelectionStore.getState().clearChecked();

  // Clear filter stores (panel open state + active filters)
  useScreenFilterStore.getState().clearFilters();
  useScreenFilterStore.setState({ isFilterPanelOpen: false });
  useAnalyticsFilterStore.getState().clearFilters();
  useAnalyticsFilterStore.setState({ isFilterPanelOpen: false });

  // Wipe favorites so the next session never briefly sees the prior account's stars before
  // its favorites query reconciles.
  useFavoritesStore.getState().reset();

  // Clear playlist asset picker path (module-level variable survives SPA navigation)
  resetPickerPath();
}

/**
 * Complete logout: clears all account data (clearAccountData), then the signed-in identity —
 * cookies, Meteor tokens, per-person hints — and signs out from Firebase.
 */
export async function fullLogout(
  client: ApolloClient,
  options?: { markExplicitLogout?: boolean },
): Promise<void> {
  if (logoutInProgress) return;
  logoutInProgress = true;
  try {
    // Clear cookies (keep __Host-scio_ver — next user's login will re-evaluate)
    clearSsoCookies();

    // Clear Meteor login tokens (prevents scio1 auto-login as previous user
    // when CF Worker redirects to scio1 after version mismatch)
    localStorage.removeItem('Meteor.userId');
    localStorage.removeItem('Meteor.loginToken');
    localStorage.removeItem('Meteor.loginTokenExpires');

    // Per-person data that could leak on shared computers
    localStorage.removeItem('optisigns_magic_link_email');

    // Clear the OneDrive picker's cached login hint (`odpickerv7cache`).
    // It persists per-origin, so without this the next user on a shared browser would get the
    // PREVIOUS user's OneDrive account preselected in the file picker. Inlined (not imported from
    // the smallapp feature) to keep lib free of feature→lib reverse imports.
    Object.keys(localStorage)
      .filter((k) => k.toLowerCase().startsWith('odpicker'))
      .forEach((k) => localStorage.removeItem(k));

    await clearAccountData(client);

    // Mark explicit logout so LoginPage ignores ProtectedRoute's `from` state
    // (prevents cross-user route leakage — User A's last page leaking to User B).
    // Skipped for cleanup-only calls (e.g. LoginPage/SignupPage validation failures)
    // to avoid poisoning the next legitimate `from` state (e.g. public feeds redirect).
    if (options?.markExplicitLogout !== false) {
      sessionStorage.setItem('scio3_explicit_logout', '1');
    }

    // Track logout across PostHog + Matomo, reset identities
    analyticsLogout();

    // Clear Zustand auth state (the signed-in session). NOT part of clearAccountData: an
    // account switch keeps the session, and clearing it would make ProtectedRoute bounce the
    // user to /signIn before the switch reloads into the new account.
    useAuthStore.getState().clearAuth();

    // Sign out from Firebase (idempotent - safe if already signed out)
    await signOut(auth);
  } finally {
    logoutInProgress = false;
  }
}
